import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Plus, Trash2, Edit2, CheckCircle2, AlertTriangle, Clock, ShieldCheck,
  Loader2, X, BarChart3, Search, Globe, Facebook, Linkedin, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const ACCESS_PLATFORMS = [
  { id: 'ga', label: 'Google Analytics', Icon: BarChart3, perms: ['Viewer', 'Analyst', 'Editor', 'Administrator'] },
  { id: 'gsc', label: 'Google Search Console', Icon: Search, perms: ['Restricted', 'Full', 'Owner'] },
  { id: 'website', label: 'Website', Icon: Globe, perms: ['Admin', 'Editor', 'FTP/Hosting'] },
  { id: 'meta', label: 'Meta (Facebook/Instagram)', Icon: Facebook, perms: ['Analyst', 'Advertiser', 'Admin'] },
  { id: 'linkedin', label: 'LinkedIn', Icon: Linkedin, perms: ['Analyst', 'Content Admin', 'Super Admin'] },
];
const platformMeta = (id) => ACCESS_PLATFORMS.find((p) => p.id === id) || ACCESS_PLATFORMS[0];

const STATUS = {
  requested: { label: 'Access Requested', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Clock },
  granted: { label: 'Granted — Awaiting Verification', cls: 'bg-blue-100 text-blue-700 border-blue-200', Icon: Send },
  verified: { label: 'Access Verified', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  issue: { label: 'Access Issue', cls: 'bg-red-100 text-red-700 border-red-200', Icon: AlertTriangle },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.requested;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`} data-testid={`access-status-${status}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
}

const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 transition';

function RequestForm({ initial, defaults, onSave, onCancel, saving }) {
  const [platform, setPlatform] = useState(initial?.platform || 'ga');
  const meta = platformMeta(platform);
  const presetPerm = initial?.permission_level && !meta.perms.includes(initial.permission_level) ? 'Custom' : (initial?.permission_level || meta.perms[0]);
  const [permMode, setPermMode] = useState(presetPerm);
  const [customPerm, setCustomPerm] = useState(initial && !meta.perms.includes(initial.permission_level) ? initial.permission_level : '');
  const [email, setEmail] = useState(initial?.grant_to_email || '');
  const [instructions, setInstructions] = useState(initial?.instructions || '');

  useEffect(() => {
    if (!initial) {
      const d = defaults?.[platform];
      if (d) setEmail(d);
      setPermMode(platformMeta(platform).perms[0]);
    }
  }, [platform]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    const permission_level = permMode === 'Custom' ? customPerm.trim() : permMode;
    onSave({ platform, grant_to_email: email.trim(), permission_level, instructions: instructions.trim() });
  };

  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-5 space-y-4" data-testid="access-request-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-slate-800 mb-1">Platform</label>
          <select className={inputCls} value={platform} onChange={(e) => setPlatform(e.target.value)} disabled={!!initial} data-testid="access-platform-select">
            {ACCESS_PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-800 mb-1">Permission level required</label>
          <select className={inputCls} value={permMode} onChange={(e) => setPermMode(e.target.value)} data-testid="access-permission-select">
            {meta.perms.map((p) => <option key={p} value={p}>{p}</option>)}
            <option value="Custom">Custom…</option>
          </select>
        </div>
      </div>
      {permMode === 'Custom' && (
        <input className={inputCls} placeholder="Enter custom permission level" value={customPerm} onChange={(e) => setCustomPerm(e.target.value)} data-testid="access-permission-custom" />
      )}
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Email / account to grant access to</label>
        <input className={inputCls} placeholder="team@lawagency360.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="access-grant-email" />
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-800 mb-1">Instructions for the client <span className="font-normal text-slate-400">(optional)</span></label>
        <textarea rows={3} className={inputCls} placeholder="e.g. In GA4 → Admin → Property Access Management → add this email as Viewer." value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="access-instructions" />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700" data-testid="access-form-cancel">Cancel</button>
        <button type="button" onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-violet-600/30 disabled:opacity-50" data-testid="access-form-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {initial ? 'Save changes' : 'Send request'}
        </button>
      </div>
    </div>
  );
}

export default function AccessRequests({ firmId, isStaff }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaults, setDefaults] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [issueFor, setIssueFor] = useState(null);
  const [issueNote, setIssueNote] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/access-requests/${firmId}`, { headers: authHeader() });
      setItems(res.data || []);
      if (isStaff) {
        try {
          const d = await axios.get(`${API}/access-defaults`, { headers: authHeader() });
          setDefaults(d.data || {});
        } catch (_e) { /* non-fatal */ }
      }
    } catch (e) { setError('Could not load access requests.'); }
    finally { setLoading(false); }
  }, [firmId, isStaff]);

  useEffect(() => { load(); }, [load]);

  const createOrUpdate = async (payload) => {
    setSaving(true); setError('');
    try {
      if (editing) await axios.put(`${API}/access-requests/${firmId}/${editing.id}`, payload, { headers: authHeader() });
      else await axios.post(`${API}/access-requests/${firmId}`, payload, { headers: authHeader() });
      setShowForm(false); setEditing(null);
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Could not save the request.'); }
    finally { setSaving(false); }
  };

  const markGranted = async (id) => {
    setBusyId(id); setError('');
    try { await axios.post(`${API}/access-requests/${firmId}/${id}/granted`, {}, { headers: authHeader() }); await load(); }
    catch (e) { setError('Could not update. Please try again.'); }
    finally { setBusyId(null); }
  };

  const resolve = async (id, status, note = '') => {
    setBusyId(id); setError('');
    try { await axios.post(`${API}/access-requests/${firmId}/${id}/resolve`, { status, issue_note: note }, { headers: authHeader() }); setIssueFor(null); setIssueNote(''); await load(); }
    catch (e) { setError('Could not update status.'); }
    finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this access request?')) return;
    setBusyId(id);
    try { await axios.delete(`${API}/access-requests/${firmId}/${id}`, { headers: authHeader() }); await load(); }
    catch (e) { setError('Could not remove.'); }
    finally { setBusyId(null); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-400 p-6" data-testid="access-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-5" data-testid="access-requests">
      {error && <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600" data-testid="access-error">{error}</div>}

      {isStaff && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Request and track the access your team needs from this client.</p>
          {!showForm && (
            <button type="button" onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-violet-600/30" data-testid="access-new-request-btn">
              <Plus className="w-4 h-4" /> New request
            </button>
          )}
        </div>
      )}

      {isStaff && showForm && (
        <RequestForm initial={editing} defaults={defaults} saving={saving} onCancel={() => { setShowForm(false); setEditing(null); }} onSave={createOrUpdate} />
      )}

      {items.length === 0 && !showForm && (
        <div className="text-center py-12 rounded-2xl border border-dashed border-slate-200 bg-slate-50" data-testid="access-empty">
          <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">{isStaff ? 'No access requests yet. Create one to get started.' : 'No access requests right now — you’re all set.'}</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((it) => {
          const meta = platformMeta(it.platform);
          const Icon = meta.Icon;
          const canClientAct = !isStaff && (it.status === 'requested' || it.status === 'issue');
          return (
            <div key={it.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`access-card-${it.id}`} data-platform={it.platform}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5 text-violet-600" /></div>
                  <div>
                    <div className="font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{it.platform_label}</div>
                    <div className="text-sm text-slate-500">Grant to <span className="font-medium text-slate-700">{it.grant_to_email || '—'}</span> · {it.permission_level || '—'}</div>
                  </div>
                </div>
                <StatusBadge status={it.status} />
              </div>

              {it.instructions && (
                <div className="mt-3 text-sm text-slate-600 bg-slate-50 border border-slate-100 rounded-xl p-3" data-testid={`access-instructions-${it.id}`}>{it.instructions}</div>
              )}
              {it.status === 'issue' && it.issue_note && (
                <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-3" data-testid={`access-issue-note-${it.id}`}>
                  <span className="font-semibold">Issue: </span>{it.issue_note}
                </div>
              )}

              {/* Client action */}
              {canClientAct && (
                <div className="mt-4 flex justify-end">
                  <button type="button" disabled={busyId === it.id} onClick={() => markGranted(it.id)} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/30 disabled:opacity-50" data-testid={`access-granted-btn-${it.id}`}>
                    {busyId === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} I’ve Granted Access
                  </button>
                </div>
              )}

              {/* Staff controls */}
              {isStaff && (
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    {it.status !== 'verified' && (
                      <button type="button" disabled={busyId === it.id} onClick={() => resolve(it.id, 'verified')} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-1.5 text-xs font-semibold border border-emerald-200 disabled:opacity-50" data-testid={`access-verify-btn-${it.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Mark Verified
                      </button>
                    )}
                    {it.status !== 'issue' && (
                      <button type="button" onClick={() => { setIssueFor(it.id); setIssueNote(it.issue_note || ''); }} className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 text-xs font-semibold border border-red-200" data-testid={`access-issue-btn-${it.id}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> Flag Issue
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => { setEditing(it); setShowForm(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="p-2 text-slate-400 hover:text-violet-600" title="Edit" data-testid={`access-edit-btn-${it.id}`}><Edit2 className="w-4 h-4" /></button>
                    <button type="button" onClick={() => remove(it.id)} className="p-2 text-slate-400 hover:text-red-500" title="Remove" data-testid={`access-delete-btn-${it.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>

                  {issueFor === it.id && (
                    <div className="w-full mt-2 flex items-center gap-2" data-testid={`access-issue-form-${it.id}`}>
                      <input className={inputCls} placeholder="Describe the issue for the client…" value={issueNote} onChange={(e) => setIssueNote(e.target.value)} data-testid={`access-issue-input-${it.id}`} />
                      <button type="button" disabled={busyId === it.id} onClick={() => resolve(it.id, 'issue', issueNote)} className="rounded-lg bg-red-600 hover:bg-red-500 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 flex-shrink-0" data-testid={`access-issue-save-${it.id}`}>Save</button>
                      <button type="button" onClick={() => { setIssueFor(null); setIssueNote(''); }} className="p-2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
